import './initPdfWorker';

import { usePrevious } from '@vkontakte/vkui/dist/hooks/usePrevious';
import classNames from 'clsx';
import debounce from 'lodash.debounce';
import type { PDFDocumentProxy } from 'pdfjs-dist/types/src/display/api';
import React, { forwardRef, ForwardRefRenderFunction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Document, Page, PDFPageProxy } from 'react-pdf';
import { noop } from 'reactApp/utils/helpers';

import styles from './PdfViewer.css';

export type PdfViewerProps = {
    content: Uint8Array;
    pageClassName?: string;
    selectedClassName?: string;
    saveLoaded?: boolean;
    mobile?: boolean;
    contentOffsets?: number[];
    pagesMargin?: number;
    currentPage?: number;
    selectedPage?: number;
    onError?: (error: string) => void;
    onPageClick?: (event: React.MouseEvent, page: PDFPageProxy) => void;
    onCurrentPageChange?: (num: number) => void;
    zoomScale?: number;
};

const DEFAULT_TOP_OFFSET = 68;
const DEFAULT_BOTTOM_OFFSET = 50;
const DEFAULT_SIDES_OFFSET = 10;

const PdfViewerComponent: ForwardRefRenderFunction<HTMLDivElement, PdfViewerProps> = (
    {
        pageClassName,
        selectedClassName,
        selectedPage,
        currentPage,
        content,
        onError,
        onPageClick = noop,
        onCurrentPageChange,
        pagesMargin = 10,
        contentOffsets,
        mobile = false,
        saveLoaded = false,
        zoomScale = 1,
    },
    ref
) => {
    const contentRef = useRef<HTMLDivElement>(null);
    const [pagesRefs, setPagesRefs] = useState<Record<number, HTMLDivElement | undefined>>([]);
    const [numPages, setNumPages] = useState(0);
    const [page, setPage] = useState(currentPage || 1);
    const [pagesHeights, setPagesHeights] = useState<Record<number, number>>({});
    const [renderPages, setRenderPages] = useState<Array<number>>([]);
    const [width, setWidth] = useState(0);
    const prevWidth = usePrevious(width);
    const [bufferPages, setBufferPages] = useState(1);
    const [waitRender, setWaitRender] = useState(false);
    const [calcCurrentPage, setCalcCurrentPage] = useState(false);
    const [calcPageHeight, setCalcPageHeight] = useState(0);

    const scrollToPage = useCallback(
        (scrollTo: number) => {
            const scrollPage = pagesRefs[scrollTo];

            if (scrollPage) {
                scrollPage.scrollIntoView(true);
            }
        },
        [pagesRefs]
    );

    useEffect(() => {
        if (!prevWidth) {
            setWaitRender(false);
            return;
        }

        const newHeights = {};
        Object.entries(pagesRefs).forEach(([num, ref]) => {
            if (!num || !ref) {
                return;
            }
            const isRealPage = ref.children.item(0)?.classList.contains('react-pdf__Page');
            if (isRealPage) {
                newHeights[num] = ref.getBoundingClientRect().height;
            } else {
                newHeights[num] = (pagesHeights[num] - pagesMargin) * (width / prevWidth) + pagesMargin;
            }
        });

        setPagesHeights(newHeights);

        setTimeout(() => {
            scrollToPage(page);
            setWaitRender(false);
        }, 100);
    }, [width]);

    useEffect(() => {
        return () => {
            setPagesRefs({});
        };
    }, []);

    useEffect(() => {
        if (ref && contentRef.current && typeof ref === 'function') {
            ref(contentRef.current);
        } else if (ref && contentRef.current && typeof ref === 'object') {
            ref.current = contentRef.current;
        }
    }, [ref, contentRef.current]);

    useEffect(() => {
        if (currentPage) {
            setPage((value) => {
                if (currentPage !== value) {
                    scrollToPage(currentPage);
                }
                return currentPage;
            });
        }
    }, [currentPage]);

    useEffect(() => {
        if (!contentRef.current || !selectedPage) {
            return;
        }
        const page = pagesRefs[selectedPage];
        if (page) {
            const { top: pageTop, height: pageHeight } = page.getBoundingClientRect();
            const { top: contentTop, height: contentHeight } = contentRef.current.getBoundingClientRect();

            if (pageTop < contentTop) {
                page.scrollIntoView(true);
            } else if (pageTop + pageHeight > contentTop + contentHeight) {
                page.scrollIntoView(false);
            }
        }
    }, [selectedPage, contentRef.current]);

    useEffect(() => {
        onCurrentPageChange?.(page);
    }, [page, onCurrentPageChange]);

    useEffect(() => {
        if (!page || !bufferPages || !numPages) {
            return;
        }
        const pages: number[] = [];

        for (let i = page - bufferPages; i <= page + bufferPages; i++) {
            if (i > 0 && i <= numPages) {
                pages.push(i);
            }
        }

        if (saveLoaded) {
            setRenderPages((prev) => {
                return prev
                    .concat(pages)
                    .filter((value, index, self) => self.indexOf(value) === index)
                    .sort((a, b) => a - b);
            });
        } else {
            setRenderPages(pages);
        }
    }, [page, bufferPages, numPages, saveLoaded]);

    useEffect(() => {
        if (!calcCurrentPage) {
            return;
        }

        // wait until pages fully render
        if (!saveLoaded && waitRender) {
            setCalcCurrentPage(false);
            return;
        }
        const currentPage = pagesRefs[page];
        if (!currentPage || !contentRef.current) {
            setCalcCurrentPage(false);
            return;
        }
        const { top, height: pageHeight } = currentPage.getBoundingClientRect();
        const { top: contentTop, height: contentHeight } = contentRef.current.getBoundingClientRect();

        if (!saveLoaded) {
            const pagePerContent = contentHeight / pageHeight;
            setBufferPages(Math.ceil((pagePerContent + 1) / 2));
        }

        const pageTop = top - 10;
        const pageBottom = pageTop + pageHeight + 10;
        const contentCenter = contentTop + contentHeight / 2;

        if (pageBottom < contentCenter) {
            const pagesToJump = Math.ceil((contentCenter - pageBottom) / pageHeight);
            const newPageNum = Math.min(numPages, page + pagesToJump);
            if (pagesRefs[newPageNum]) {
                setPage(newPageNum);
                setWaitRender(true);
            }
        }
        if (pageTop > contentCenter) {
            const pagesToJump = Math.ceil((pageTop - contentCenter) / pageHeight);
            const newPageNum = Math.max(1, page - pagesToJump);
            if (pagesRefs[newPageNum]) {
                setPage(newPageNum);
                setWaitRender(true);
            }
        }

        setCalcCurrentPage(false);
    }, [calcCurrentPage]);

    const pageSideOffset = useMemo(() => {
        return contentOffsets?.[1] || contentOffsets?.[0] || DEFAULT_SIDES_OFFSET;
    }, [contentOffsets]);

    const calcWidth = useCallback(() => {
        if (!contentRef.current) {
            return;
        }
        const { width } = contentRef.current.getBoundingClientRect();
        const pageWidth = width - pageSideOffset * 2;
        setWidth(pageWidth);
    }, [contentRef.current, pageSideOffset]);

    const debounceCalcWidth = useCallback(debounce(calcWidth, 100), [calcWidth]);

    const handleResize = useCallback(() => {
        setWaitRender(true);
        debounceCalcWidth();
    }, [debounceCalcWidth]);

    useEffect(() => {
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [handleResize]);

    useEffect(() => {
        setTimeout(() => {
            if (pagesRefs[page]) {
                setWaitRender(false);
                setCalcCurrentPage(true);
            }
        }, 300);
    }, [pagesRefs, page]);

    useEffect(() => {
        if (!contentRef.current) {
            return;
        }

        const fn = () => setCalcCurrentPage(true);

        contentRef.current.addEventListener('scroll', fn);

        return () => {
            contentRef.current?.removeEventListener('scroll', fn);
        };
    }, [contentRef.current]);

    const file = useMemo(() => ({ data: content }), [content]);

    useEffect(() => {
        const ref = pagesRefs[calcPageHeight];
        if (!ref) {
            return;
        }
        const height = ref.getBoundingClientRect().height;
        const isRealPage = ref.children.item(0)?.classList.contains('react-pdf__Page');

        if (height && isRealPage) {
            setPagesHeights((pagesHeights) => ({ ...pagesHeights, [calcPageHeight]: height / zoomScale }));
        }
    }, [calcPageHeight]);

    const handleRef = useCallback((page: HTMLDivElement | null) => {
        if (!page) {
            return;
        }

        const pageNumber = Number(page.dataset.pageNumber);

        setPagesRefs((pagesRefs) => ({ ...pagesRefs, [pageNumber]: page }));

        setTimeout(() => {
            setCalcPageHeight(pageNumber);
        }, 200);
    }, []);

    const onLoadSuccess = useCallback(
        ({ numPages }: PDFDocumentProxy) => {
            calcWidth();
            setNumPages(numPages);
            setPage((value) => value || 1);
        },
        [calcWidth]
    );

    const onLoadError = useCallback((error: Error) => {
        onError?.(error.message);
    }, []);

    const defaultPageHeight = useMemo(() => {
        const values = Array.from(new Array(10).keys())
            .map((num) => pagesHeights[num])
            .filter(Boolean);

        if (!values.length) {
            return 500;
        }

        return Math.min(...values);
    }, [pagesHeights[2], pagesHeights[5], pagesHeights[10]]);

    const topBuffer = useMemo(() => {
        if (!renderPages.length) {
            return [];
        }

        const pages: Array<number> = [];

        const until = renderPages[0] || 1;

        for (let i = 1; i < until; i++) {
            pages.push(i);
        }

        return pages;
    }, [renderPages]);

    const topBufferStyles = useMemo(() => {
        const topOffset = contentOffsets?.[0] || DEFAULT_TOP_OFFSET;
        return { height: topOffset };
    }, [contentOffsets]);

    const bottomBuffer = useMemo(() => {
        if (!renderPages.length) {
            return [];
        }

        const pages: Array<number> = [];

        const lastRenderedPage = renderPages[renderPages.length - 1];
        const from = lastRenderedPage + 1;
        const until = Math.min(numPages, lastRenderedPage + 5);

        for (let i = from; i <= until; i++) {
            pages.push(i);
        }

        return pages;
    }, [numPages, renderPages]);

    const bottomBufferStyles = useMemo(() => {
        const bottomOffset = contentOffsets?.[2] || contentOffsets?.[0] || DEFAULT_BOTTOM_OFFSET;
        return { height: bottomOffset };
    }, [contentOffsets]);

    const renderBuffer = useCallback(
        (num: number) => (
            <div key={num} ref={handleRef} data-page-number={num}>
                <div className={styles.bufferPage} style={{ height: (pagesHeights[num] || defaultPageHeight) - pagesMargin, width }} />
                <div className={styles.buffer} style={{ height: pagesMargin }} />
            </div>
        ),
        [handleRef, pagesHeights, defaultPageHeight, width, pagesMargin]
    );

    return (
        <div ref={contentRef} className={classNames(styles.root, { [styles.mobile]: mobile })} body-scroll-lock-ignore="true">
            <Document
                renderMode="canvas"
                file={file}
                onLoadSuccess={onLoadSuccess}
                onLoadError={onLoadError}
                onSourceError={onLoadError}
                loading=""
            >
                {numPages && width && (
                    <>
                        <div className={styles.buffer} style={topBufferStyles} />

                        {topBuffer.map(renderBuffer)}

                        {renderPages.map((num) => (
                            <div key={num} ref={handleRef} data-page-number={num}>
                                <Page
                                    className={classNames(pageClassName, { [selectedClassName || '']: selectedPage === num })}
                                    renderTextLayer={false}
                                    renderAnnotationLayer={false}
                                    renderInteractiveForms={false}
                                    width={width}
                                    pageNumber={num}
                                    onClick={onPageClick}
                                    loading=""
                                />
                                <div className={styles.buffer} style={{ height: pagesMargin }} />
                            </div>
                        ))}

                        {bottomBuffer.map(renderBuffer)}

                        <div className={styles.buffer} style={bottomBufferStyles} />
                    </>
                )}
            </Document>
        </div>
    );
};

export const PdfViewer = forwardRef(PdfViewerComponent);
